import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "@web/contexts";
import { useEffect } from "react";
import { getProviderConnections, hasConnectedProvider } from "@/features/sync/connections";
import { api, setAccessToken } from "@/lib/api";

function storeToken(token: string | null) {
  try {
    if (token) localStorage.setItem("better-auth-session-token", token);
    else localStorage.removeItem("better-auth-session-token");
  } catch {}
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refreshSession, setSession } = useAuth();

  useEffect(() => {
    let cancelled = false;

    async function finishAuth() {
      const res = await api.get<{
        session?: { token?: string };
        user?: { id: string; email: string; name: string; image?: string | null };
      }>("/auth/get-session");
      if (res.data?.session?.token) {
        const token = res.data.session.token;
        storeToken(token);
        setAccessToken(token);
        // Trust the user this same response already vouches for rather than
        // re-verifying the brand-new session through refreshSession's
        // `/auth/session` call — that endpoint does its own direct DB read
        // and can lose a read-after-write race right after the session row
        // was created, which would otherwise wipe this valid token and show
        // a false "session expired" warning despite sign-in having worked.
        const sessionUser = res.data.user;
        if (sessionUser?.id && sessionUser.email && sessionUser.name) {
          setSession(sessionUser, token);
        } else {
          await refreshSession();
        }
        if (!cancelled) {
          let redirect: string | null = null;
          try {
            redirect = localStorage.getItem("signin_redirect");
            if (redirect) localStorage.removeItem("signin_redirect");
          } catch {}
          const destination = redirect ?? "/";
          const connections = await getProviderConnections();
          if (!cancelled && !hasConnectedProvider(connections)) {
            navigate({
              to: "/health-setup",
              search: { redirect: destination },
              replace: true,
            });
          } else if (!cancelled) {
            navigate({ to: destination, replace: true });
          }
        }
      } else {
        if (!cancelled) navigate({ to: "/sign-in", replace: true });
      }
    }

    const params =
      typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
    const error = params?.get("error");
    if (error) {
      if (!cancelled) navigate({ to: "/sign-in", replace: true });
    } else {
      finishAuth();
    }

    return () => {
      cancelled = true;
    };
  }, [navigate, refreshSession, setSession]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-8 flex-col gap-3">
      <h1 className="text-text text-2xl font-extrabold text-center">Completing sign-in</h1>
      <div className="w-9 h-9 border-3 border-[#ff2d7e33] border-t-[#ff2d7e] rounded-full animate-spin" />
      <p className="text-text-muted text-base text-center leading-6">
        This should only take a moment
      </p>
    </div>
  );
}
